import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

import { cn } from "@/lib/utils";
import { FloatingSurfaceYieldSentinel } from "@/components/ui/floating-surface";
import { IoCloseOutline } from "react-icons/io5";
import { DIALOG_CONTENT_FIT } from "@/components/ui/dialog-content-fit";
import { keepEditorEscape } from "@/lib/escape-ownership";
import { makeScrollBodyFocusable } from "@/lib/dialog-scroll-body";
import { DIALOG_SUBMIT_ATTRIBUTE, findShortcutTarget, isSubmitShortcut } from "@/lib/dialog-shortcuts";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
    testId?: string;
  }
>(({ className, testId, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-dialog bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    data-testid={testId}
    {...props}
  />
));
DialogOverlay.displayName = "DialogOverlay";

/** The dialog's X. Absolute against its nearest positioned ancestor, so a sticky header can hold it while the box scrolls. */
const DialogCloseButton = ({ testId = "dialog" }: { testId?: string }) => (
  <DialogPrimitive.Close
    className="absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 disabled:pointer-events-none data-[state=open]:text-muted-foreground mobile:flex mobile:h-11 mobile:w-11 mobile:items-center mobile:justify-center mobile:right-2 mobile:top-2"
    data-testid={`${testId}-close`}
  >
    <IoCloseOutline className="h-4 w-4 mobile:h-6 mobile:w-6" />
    <span className="sr-only">Close</span>
  </DialogPrimitive.Close>
);
DialogCloseButton.displayName = "DialogCloseButton";

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    closeButton?: boolean;
    testId?: string;
  }
>(
  (
    {
      className,
      children,
      closeButton = true,
      testId = "dialog",
      onEscapeKeyDown,
      onKeyDown,
      ...props
    },
    ref
  ) => (
    <DialogPortal>
      <DialogOverlay data-testid={`${testId}-overlay`} />
      <DialogPrimitive.Content
        ref={ref}
        onEscapeKeyDown={keepEditorEscape(onEscapeKeyDown)}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          // BRE-9251: Mod+Enter presses this dialog's submit button. The DOM
          // check keeps a nested dialog (portaled, so outside this element but
          // inside its React tree) from submitting the one behind it.
          if (!isSubmitShortcut(event.nativeEvent) || !event.currentTarget.contains(event.target as Node)) return;
          const submit = findShortcutTarget(event.currentTarget, DIALOG_SUBMIT_ATTRIBUTE);
          if (!submit) return;
          event.preventDefault();
          submit.click();
        }}
        {...props}
      >
        {/* BRE-4647: while this dialog is open, drop the floating surface it
            was opened from (the agent bubble) below `z-dialog` - it otherwise
            covers the dialog it spawned. Inert outside a FloatingSurface. Must
            live inside DialogPrimitive.Content: that's what Radix mounts only
            while open. */}
        <FloatingSurfaceYieldSentinel />
        {/* BRE-9762: focusable so a click inside lands focus here, not on the
            Radix wrapper above, and PageDown/arrows scroll this box. BRE-9763:
            a click inside a nested scroll body focuses that body instead. */}
        <div
          tabIndex={-1}
          onPointerDown={(event) => makeScrollBodyFocusable(event.target, event.currentTarget)}
          className={cn(
            `fixed left-[50%] top-[50%] z-dialog grid mb-20 min-w-96 max-w-lg max-h-[85vh] overflow-y-auto outline-none translate-x-[-50%] translate-y-[-50%] gap-4 border border-border bg-background p-6 shadow-xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-2xl`,
            DIALOG_CONTENT_FIT,
            // Mobile: render as a content-fitting bottom drawer instead of
            // a full-screen modal. Auto-height up to 85dvh, anchored to
            // the bottom edge with safe-area inset padding so primary
            // actions are thumb-reachable. Override the desktop
            // top/center slide animation with a bottom slide so the
            // drawer rises from the bottom edge (iOS sheet convention).
            // `mobile:!min-w-0` is load-bearing: downstream dialogs
            // (e.g. BaseDialog with `min-w-[750px]`) set a desktop
            // minimum width that would otherwise force horizontal
            // overflow on phones — `min-width` wins over `width` in
            // CSS, so `w-screen` alone isn't enough.
            "mobile:inset-x-0 mobile:left-0 mobile:bottom-0 mobile:top-auto mobile:m-0 mobile:h-auto mobile:max-h-[85dvh] mobile:w-screen mobile:max-w-none mobile:!min-w-0 mobile:translate-x-0 mobile:translate-y-0 mobile:rounded-t-2xl mobile:rounded-b-none mobile:border-x-0 mobile:border-b-0 mobile:p-5 mobile:pb-[max(1.25rem,env(safe-area-inset-bottom))] mobile:overflow-y-auto mobile:data-[state=closed]:slide-out-to-left-0 mobile:data-[state=closed]:slide-out-to-top-0 mobile:data-[state=open]:slide-in-from-left-0 mobile:data-[state=open]:slide-in-from-top-0 mobile:data-[state=open]:slide-in-from-bottom-[10%] mobile:data-[state=closed]:slide-out-to-bottom-[10%]",
            className
          )}
          data-testid={`${testId}-content`}
        >
          {children}
          {closeButton && <DialogCloseButton testId={testId} />}
        </div>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
);
DialogContent.displayName = "DialogContent";

const DialogHeader = ({
  className,
  testId = "dialog-header",
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { testId?: string }) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    data-testid={testId}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  testId = "dialog-footer",
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { testId?: string }) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    data-testid={testId}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
    testId?: string;
  }
>(({ className, testId = "dialog-title", ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight text-left",
      className
    )}
    data-testid={testId}
    {...props}
  />
));
DialogTitle.displayName = "DialogTitle";

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
    testId?: string;
  }
>(({ className, testId = "dialog-description", ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground text-left", className)}
    data-testid={testId}
    {...props}
  />
));
DialogDescription.displayName = "DialogDescription";

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogCloseButton,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription
};

/** Surface for a full-viewport mode whose host owns focus, scrolling and exit keys. */
export const FullscreenSurface = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("fixed inset-0 z-dialog overflow-x-hidden bg-background outline-none", className)} {...props} />
);
FullscreenSurface.displayName = "FullscreenSurface";
